import { ChatMessage } from "@livekit/components-core"
import { useChat } from "@livekit/components-react"
import { Box, useMediaQuery, useTheme } from "@mui/material"
import { PaperPlaneTilt, X } from "@phosphor-icons/react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ChatBox } from "./ChatBox"

type UseChatReturnType = ReturnType<typeof useChat>

interface CustomChatBoxProps extends UseChatReturnType {
  showChat: boolean
  closeChat: () => void
}

interface ChatInputProps {
  text: string
  setText: (text: string) => void
  handleSend: () => void
}

const ChatInput = React.memo(
  ({ text, setText, handleSend }: ChatInputProps) => {
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        handleSend()
      }
    }

    return (
      <Box
        data-testid="chat-input-container"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
          paddingLeft: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
          paddingRight: "var(--spacing-tokens-size-in-px-spacing-spacing-xs)",
          paddingY: "var(--spacing-tokens-size-in-px-spacing-spacing-xs)",
          borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-3xl)",
          backgroundColor: "var(--call-field-bg)",
        }}
      >
        <Box
          component="input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Send a message..."
          name="chat-input"
          autoComplete="off"
          autoFocus
          data-testid="chat-input-field"
          sx={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            fontSize: "var(--global-text-m-regular-font-size)",
            lineHeight: "calc(var(--global-text-m-regular-line-height) * 1px)",
            color: "var(--call-field-text)",
            "&::placeholder": { color: "var(--call-field-placeholder)" },
          }}
        />
        <Box
          component="button"
          type="button"
          aria-label="Send message"
          onClick={handleSend}
          data-testid="chat-send-button"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: 40,
            height: 40,
            border: "none",
            borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-full)",
            backgroundColor: "var(--call-send-bg)",
            color: "var(--call-send-fg)",
            cursor: "pointer",
          }}
        >
          <PaperPlaneTilt size={20} />
        </Box>
      </Box>
    )
  },
)

interface ChatPanelProps extends ChatInputProps {
  showChat: boolean
  closeChat: () => void
  chatMessages: ChatMessage[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  messagesEndRef: React.RefObject<HTMLDivElement>
}

const ChatPanel = React.memo(
  ({
    showChat,
    closeChat,
    chatMessages,
    chatContainerRef,
    messagesEndRef,
    text,
    setText,
    handleSend,
  }: ChatPanelProps) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    if (!showChat) return null

    return (
      <Box
        sx={{
          position: "absolute",
          zIndex: 7,
          top: isMobile
            ? "auto"
            : "var(--spacing-tokens-size-in-px-spacing-spacing-8xl)",
          right: "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)",
          bottom: "var(--spacing-tokens-size-in-px-spacing-spacing-9xl)",
          left: isMobile
            ? "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)"
            : "auto",
          width: isMobile ? "auto" : 356,
          maxHeight: isMobile ? "50%" : "none",
          display: "flex",
          flexDirection: "column",
          borderRadius: "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)",
          backgroundColor: "var(--call-panel-bg)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingX: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
            paddingY: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            borderBottom: "1px solid var(--call-panel-divider)",
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            fontSize: "var(--global-heading-s-semi-bold-font-size)",
            fontWeight: "var(--global-heading-s-semi-bold-font-weight)",
            lineHeight:
              "calc(var(--global-heading-s-semi-bold-line-height) * 1px)",
            color: "var(--color-text-text-white)",
          }}
        >
          Conversation
          <Box
            component="button"
            type="button"
            aria-label="Close conversation"
            onClick={closeChat}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
              border: "none",
              borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-xs)",
              background: "transparent",
              color: "var(--color-foreground-fg-white)",
              cursor: "pointer",
            }}
          >
            <X size={20} />
          </Box>
        </Box>

        <Box
          ref={chatContainerRef}
          data-testid="chat-message-container"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            paddingX: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
            paddingY: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              marginTop: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
            }}
          >
            {chatMessages.map((message) => (
              <ChatBox key={message.id} item={message} />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        </Box>

        <Box
          sx={{
            padding: "var(--spacing-tokens-size-in-px-spacing-spacing-xl)",
            borderTop: "1px solid var(--call-panel-divider)",
          }}
        >
          <ChatInput text={text} setText={setText} handleSend={handleSend} />
        </Box>
      </Box>
    )
  },
)

const CustomChatBoxComponent = ({
  showChat,
  closeChat,
  chatMessages,
  send,
}: CustomChatBoxProps) => {
  const [text, setText] = useState("")
  const [isNearBottom, setIsNearBottom] = useState(true)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const checkIfNearBottom = useCallback(() => {
    const container = chatContainerRef.current
    if (!container) return true

    const threshold = 150
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    )
  }, [])

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior })
      }
    },
    [isNearBottom],
  )

  const handleScroll = useCallback(() => {
    setIsNearBottom(checkIfNearBottom())
  }, [checkIfNearBottom])

  useEffect(() => {
    const container = chatContainerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll, showChat])

  useEffect(() => {
    scrollToBottom("smooth")
  }, [chatMessages.length, scrollToBottom])

  useEffect(() => {
    if (showChat) {
      scrollToBottom()
    }
  }, [showChat, scrollToBottom])

  const handleSend = useCallback(async () => {
    if (text.trim() === "") return

    await send(text)

    setText("")
    setIsNearBottom(true)
    setTimeout(() => scrollToBottom("smooth"), 100)
  }, [text, send, scrollToBottom])

  return (
    <ChatPanel
      showChat={showChat}
      closeChat={closeChat}
      chatMessages={chatMessages}
      chatContainerRef={chatContainerRef}
      messagesEndRef={messagesEndRef}
      text={text}
      setText={setText}
      handleSend={handleSend}
    />
  )
}

export const CustomChatBox = React.memo(
  CustomChatBoxComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.showChat === nextProps.showChat &&
      prevProps.isSending === nextProps.isSending &&
      prevProps.chatMessages === nextProps.chatMessages
    )
  },
)
